import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PRODUCTS } from '../constants';
const CATEGORY_DATA = {
    "Men's Fashion": [
        { title: "Summer Style", img: "https://images.unsplash.com/photo-1543163521-1bf539c55dd2?auto=format&fit=crop&q=80&w=800" },
        { title: "Art of Beat", img: "https://images.unsplash.com/photo-1617137968427-85924c800a22?auto=format&fit=crop&q=80&w=800" },
        { title: "Street Style", img: "https://images.unsplash.com/photo-1550246140-5119ae4790b8?auto=format&fit=crop&q=80&w=800" },
        { title: "Classic Elegant", img: "https://images.unsplash.com/photo-1594938298603-c8148c4dae35?auto=format&fit=crop&q=80&w=800" }
    ],
    "Women's Fashion": [
        { title: "Urban Chic", img: "https://images.unsplash.com/photo-1564584217132-2271feaeb3c5?auto=format&fit=crop&q=80&w=800" },
        { title: "Minimalist", img: "https://images.unsplash.com/photo-1552374196-1ab2a1c593e8?auto=format&fit=crop&q=80&w=800" },
        { title: "Evening Wear", img: "https://images.unsplash.com/photo-1629737168222-baad8ecacebe?auto=format&fit=crop&q=80&w=800" },
        { title: "Casual Flow", img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&q=80&w=800" }
    ],
    "Kids' Fashion": [
        { title: "Playground", img: "https://images.unsplash.com/photo-1621452773781-0f992fd1f5cb?auto=format&fit=crop&q=80&w=800" },
        { title: "Mini Classic", img: "https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?auto=format&fit=crop&q=80&w=800" },
        { title: "Active Kids", img: "https://images.unsplash.com/photo-1519238263530-99bdd11df2ea?auto=format&fit=crop&q=80&w=800" },
        { title: "Party Time", img: "https://images.unsplash.com/photo-1519689680058-324335c77eba?auto=format&fit=crop&q=80&w=800" }
    ]
};

export const FashionCategoryGrid = () => {
    const navigate = useNavigate();
    const [selectedCategory, setSelectedCategory] = React.useState<keyof typeof CATEGORY_DATA>("Men's Fashion");
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    const handleItemClick = (title: string, category: keyof typeof CATEGORY_DATA) => {
        const product = PRODUCTS.find(p => p.name === title);
        if (product) {
            navigate(`/products/${product.id}`);
        } else {
            const genderMap: any = {
                "Men's Fashion": "man",
                "Women's Fashion": "woman",
                "Kids' Fashion": "kids"
            };
            navigate(`/category/${genderMap[category]}`);
        }
    };

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const categories = Object.keys(CATEGORY_DATA) as Array<keyof typeof CATEGORY_DATA>;
    const currentItems = CATEGORY_DATA[selectedCategory];

    return (
        <section className="mb-24">
            <div className="border-t-[1.5px] border-black mb-6"></div>
            <div className="flex justify-between items-center mb-10">
                <h2 className="font-heading text-4xl md:text-6xl uppercase font-black tracking-tighter">Fashion Category</h2>
                <div className="relative" ref={dropdownRef}>
                    <button
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="text-xs font-bold uppercase border border-gray-300 px-4 py-2 rounded-full cursor-pointer hover:border-black transition-colors flex items-center gap-2 bg-white"
                    >
                        {selectedCategory} <span className={`text-[10px] transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`}>▼</span>
                    </button>

                    {/* Dropdown Menu */}
                    <div className={`absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden z-20 transition-all duration-300 origin-top-right ${isDropdownOpen ? 'opacity-100 scale-100 translate-y-0 visible' : 'opacity-0 scale-95 -translate-y-2 invisible'}`}>
                        {categories.map((cat) => (
                            <button
                                key={cat}
                                onClick={() => {
                                    setSelectedCategory(cat);
                                    setIsDropdownOpen(false);
                                }}
                                className={`w-full text-left px-4 py-3 text-[10px] font-bold uppercase transition-colors hover:bg-black hover:text-white ${selectedCategory === cat ? 'bg-gray-100' : ''}`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Row 1 */}
            <div className="grid grid-cols-2 md:grid-cols-2 gap-4 md:gap-8 mb-10 border-b border-gray-200 pb-10">
                {currentItems.slice(0, 2).map((item, index) => (
                    <div
                        key={item.title}
                        onClick={() => handleItemClick(item.title, selectedCategory)}
                        className="relative group cursor-pointer animate-fade-in-up"
                        style={{ animationDelay: `${index * 100}ms` }}
                    >
                        <div className="overflow-hidden aspect-[3/2] mb-3">
                            <img
                                src={item.img}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                alt={item.title}
                            />
                        </div>
                        {/* Editorial Numbering & Info */}
                        <div className="flex items-center gap-3 border-b border-black pb-2 mb-2">
                            <span className="text-sm font-body font-bold">0{index + 1}</span>
                            <div className="flex-grow"></div>
                            <span className="text-sm font-bold uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-300">Explore ↗</span>
                        </div>
                        <h3 className="text-xl font-heading font-bold uppercase tracking-tight">{item.title}</h3>
                    </div>
                ))}
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-2 md:grid-cols-2 gap-4 md:gap-8">
                {currentItems.slice(2, 4).map((item, index) => (
                    <div
                        key={item.title}
                        onClick={() => handleItemClick(item.title, selectedCategory)}
                        className="relative group cursor-pointer animate-fade-in-up"
                        style={{ animationDelay: `${(index + 2) * 100}ms` }}
                    >
                        <div className="overflow-hidden aspect-[3/2] mb-3">
                            <img
                                src={item.img}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                alt={item.title}
                            />
                        </div>
                        {/* Editorial Numbering & Info */}
                        <div className="flex items-center gap-3 border-b border-black pb-2 mb-2">
                            <span className="text-sm font-body font-bold">0{index + 3}</span>
                            <div className="flex-grow"></div>
                            <span className="text-sm font-bold uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-300">Explore ↗</span>
                        </div>
                        <h3 className="text-xl font-heading font-bold uppercase tracking-tight">{item.title}</h3>
                    </div>
                ))}
            </div>
        </section>
    );
};
